// Helper functions for DiagnosticsSummary component

import {
  overlayExists,
  overlaysExist,
} from '../../../../../helpers/diagnostics/detectionOrigin';

const hasFindingsInBucket = (projectionData, type) => {
  const findings = projectionData?.[type];
  return Array.isArray(findings) && findings.length > 0;
};

export const specialTypeExists = (type, result, zoomedProjection) => {
  if (!result) return false;
  if (zoomedProjection) {
    return hasFindingsInBucket(result[zoomedProjection.toLowerCase()], type);
  }
  return Object.values(result).some((projectionData) => projectionData && hasFindingsInBucket(projectionData, type));
};

export const isOverlayShown = (type, result, overlaysToShow, showMissed) => {
  return overlaysExist(type, result, showMissed)
    ? Object.values(overlaysToShow[type]).every((value) => value === true)
    : true;
};

export const typeOfLesionsExist = (benignTypes, malignantTypes, result, zoomedProjection, showMissed) => {
  const benignExists = benignTypes.some((type) => overlayExists(type, result, zoomedProjection, showMissed));
  const malignantExists = malignantTypes.some((type) => overlayExists(type, result, zoomedProjection, showMissed));
  return { benignExists, malignantExists };
};

export const lesionExists = (obj) => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (!lesionExists(obj[key])) {
          return false;
        }
      } else if (obj[key] !== null && obj[key] !== undefined) {
        return false;
      }
    }
  }
  return true;
};

export const categoryExists = (obj) => {
  if (!obj) return true;

  const isBiradsKey = (key) => /^birads\d+$/i.test(key);

  return Object.values(obj).every((projectionData) => {
    if (!projectionData || typeof projectionData !== 'object') return true;

    return Object.entries(projectionData)
      .filter(([key]) => isBiradsKey(key))
      .every(([, value]) => {
        if (value === null || value === undefined) return true;
        if (typeof value === 'object') return lesionExists(value);
        return false;
      });
  });
};

export const createLesionObjects = (config, lesionModule, t, theme, zoomedProjection, showMissed) => {
  return Object.entries(config.types).map(([type, typeConfig]) => ({
    overlayType: t(typeConfig.chipText),
    label: t(typeConfig.chipText),
    color: theme.palette.diagnosticsClasses[typeConfig.color],
    overlaysExist: overlayExists(type, config.result, zoomedProjection, showMissed),
    isShown: isOverlayShown(type, config.result, config.overlaysToShow, showMissed),
    overlaysToShow: typeConfig.classLabel,
    tooltip_title: t(typeConfig.tooltipText),
    lesionModule,
    lesionType: typeConfig.lesionType || 'calc',
  }));
};

export const createMergedLesionsByCategory = (microcalcLesions, opacitiesLesions, t) => {
  const biradsCategories = ['birads2', 'birads3', 'birads4', 'birads5'];
  const mergedStructure = {};

  biradsCategories.forEach((category) => {
    const microcalcForCategory = microcalcLesions.find((lesion) => lesion.overlaysToShow === category);
    const opacitiesForCategory = opacitiesLesions.find((lesion) => lesion.overlaysToShow === category);

    mergedStructure[category] = {
      microcalc: microcalcForCategory || null,
      opacities: opacitiesForCategory || null,
      category,
      label: t(`diagnose.${category}`),
    };
  });

  return mergedStructure;
};

export const getLesionConfig = (
  microcalcificationsResult,
  opacitiesResult,
  microcalcOverlaysToShow,
  opacitiesOverlaysToShow,
  microcalcLesionsZoomed,
  opacitiesLesionsZoomed
) => ({
  microcalc: {
    result: microcalcificationsResult,
    overlaysToShow: microcalcOverlaysToShow,
    zoomedData: microcalcLesionsZoomed,
    benignTypes: ['birads2', 'birads3'],
    malignantTypes: ['birads4', 'birads5'],
    types: {
      birads2: {
        chipText: 'diagnose.birads_2',
        classLabel: 'birads2',
        color: 'birads2',
        tooltipText: 'diagnose.unsusp_calc',
        lesionType: 'microcalc',
      },
      birads3: {
        chipText: 'diagnose.birads_3',
        classLabel: 'birads3',
        color: 'birads3',
        tooltipText: 'diagnose.unsusp_calc',
        lesionType: 'microcalc',
      },
      birads4: {
        chipText: 'diagnose.birads_4',
        classLabel: 'birads4',
        color: 'birads4',
        tooltipText: 'diagnose.susp_calc',
        lesionType: 'microcalc',
      },
      birads5: {
        chipText: 'diagnose.birads_5',
        classLabel: 'birads5',
        color: 'birads5',
        tooltipText: 'diagnose.susp_calc',
        lesionType: 'microcalc',
      },
    },
  },
  opacities: {
    result: opacitiesResult,
    overlaysToShow: opacitiesOverlaysToShow,
    zoomedData: opacitiesLesionsZoomed,
    benignTypes: ['birads2', 'birads3'],
    malignantTypes: ['birads4', 'birads5'],
    types: {
      birads2: {
        chipText: 'diagnose.birads_2',
        classLabel: 'birads2',
        color: 'birads2',
        tooltipText: 'diagnose.birads2',
        lesionType: 'opacities',
      },
      birads3: {
        chipText: 'diagnose.birads_3',
        classLabel: 'birads3',
        color: 'birads3',
        tooltipText: 'diagnose.birads3',
        lesionType: 'opacities',
      },
      birads4: {
        chipText: 'diagnose.birads_4',
        classLabel: 'birads4',
        color: 'birads4',
        tooltipText: 'diagnose.birads4',
        lesionType: 'opacities',
      },
      birads5: {
        chipText: 'diagnose.birads_5',
        classLabel: 'birads5',
        color: 'birads5',
        tooltipText: 'diagnose.birads5',
        lesionType: 'opacities',
      },
    },
  },
});
